/**
 * Rule Cell Utility Functions
 * Functions for parsing, manipulating, and analyzing rule cell values
 */

import { apiFetch } from '../../../utils/api';
import { compareAlphabetical } from '../../../utils/sortingUtils';

/**
 * Parse a cell value string into an array of selected values
 * Handles special markers: ?, !
 * @param {string} cellValue - The cell value to parse
 * @returns {Array} - Array of selected values (empty array for special wildcards)
 */
export const getSelectedValues = (cellValue) => {
    if (!cellValue || cellValue === "*" || cellValue === "?" || cellValue === "!") return [];
    return cellValue.split(',').map(v => v.trim()).filter(Boolean);
};

/**
 * True when a cell has no expected value. Wildcards (?, !, *) are not empty.
 * @param {string|null|undefined} cellValue
 * @returns {boolean}
 */
export const isEmptyRuleCellValue = (cellValue) => {
    if (cellValue == null) return true;
    return String(cellValue).trim() === '';
};

/**
 * True when any existing cell in the ruleset has an empty expected value.
 * @param {Object} data - Draft or saved ruleset data
 * @returns {boolean}
 */
export const rulesetHasEmptyCells = (data) => {
    if (!data?.rows?.length) return false;
    return data.rows.some(row =>
        (row.cells || []).some(cell => isEmptyRuleCellValue(cell.expectedValue ?? cell.ExpectedValue))
    );
};

/**
 * Toggle a value in a cell's selection
 * Handles special markers and multi-select logic
 * @param {string} currentVal - Current cell value
 * @param {string} valToToggle - Value to toggle
 * @returns {string} - New cell value
 */
export const toggleValue = (currentVal, valToToggle) => {
    if (valToToggle === "?") {
        return "?";
    }
    if (valToToggle === "!") {
        return "!";
    }
    let selectedList = getSelectedValues(currentVal);
    if (currentVal === "?" || currentVal === "!") {
        selectedList = [];
    }
    let newList;
    if (selectedList.includes(valToToggle)) {
        newList = selectedList.filter(v => v !== valToToggle);
    } else {
        newList = [...selectedList, valToToggle];
    }
    return newList.length === 0 ? "" : newList.join(", ");
};

/**
 * Restrict project-wide feature metadata to the active family (plus globals).
 * Empty familyIds keeps only global features so name lookups cannot pick another model's feature.
 * @param {Array} featuresMetadata - Project-wide feature catalog
 * @param {Array} familyIds - Active ruleset family ids
 * @returns {Array}
 */
export const filterFeaturesForFamily = (featuresMetadata, familyIds) => {
    const features = Array.isArray(featuresMetadata) ? featuresMetadata : [];
    const ids = Array.isArray(familyIds) ? familyIds : [];
    if (ids.length === 0) {
        return features.filter(v => v.isGlobal);
    }
    return features.filter(v =>
        v.isGlobal || (v.sourceFamilyIds && v.sourceFamilyIds.some(famId => ids.includes(famId)))
    );
};

/**
 * Family ids for scoping feature lookups: ruleset source families, else current context.
 * @param {Object} activeRuleSetData
 * @param {Object} currentContext
 * @returns {Array}
 */
export const resolveFamilyIdsForFeatureLookup = (activeRuleSetData, currentContext) => {
    const fromRuleset = activeRuleSetData?.sourceFamilyIds || [];
    if (fromRuleset.length > 0) return fromRuleset;
    if (currentContext?.id && currentContext.id !== 'global') return [currentContext.id];
    return [];
};

/**
 * Get unused values for a specific header based on current rule data
 * @param {string} headerName - The feature/column name
 * @param {Object} draftRuleSetData - Current draft ruleset data
 * @param {Array} featuresMetadata - Array of feature metadata (must already be family-scoped)
 * @returns {Array|null} - Array of unused values, or null if feature not found
 */
export const getUnusedValuesForHeader = (headerName, draftRuleSetData, featuresMetadata) => {
    const featureMeta = featuresMetadata.find(v => v.name === headerName);
    if (!featureMeta || !Array.isArray(featureMeta.values) || featureMeta.values.length === 0) {
        return null;
    }
    
    let hasNotUsedWildcard = false;
    const usedValues = new Set();
    if (draftRuleSetData && draftRuleSetData.rows) {
        draftRuleSetData.rows.forEach(row => {
            const cell = row.cells.find(c => c.featureName === headerName);
            if (cell && cell.expectedValue) {
                const expectedValueStr = String(cell.expectedValue);
                if (expectedValueStr === "*" || expectedValueStr === "!" || expectedValueStr === "?") {
                    hasNotUsedWildcard = true;
                }
                const values = getSelectedValues(expectedValueStr);
                values.forEach(v => usedValues.add(v));
            }
        });
    }
    
    if (hasNotUsedWildcard) {
        return [];
    }
    
    return featureMeta.values
        .filter(v => !usedValues.has(String(v.value)))
        .map(v => v.value !== undefined && v.value !== null ? String(v.value) : '')
        .sort(compareAlphabetical);
};

/**
 * Serialize a ruleset grid to TSV (header row + cell values in header order).
 * Looks up cells by feature name so column order matches the table, not cell array order.
 * @param {Object} data - Draft or saved ruleset data
 * @returns {string} - TSV string
 */
export const rulesetToTsv = (data) => {
    if (!data) return '';
    const headers = data.headers || data.Headers || [];
    const rows = data.rows || data.Rows || [];
    const headerLine = headers.join('\t');
    const rowLines = rows.map(row => {
        const cells = row.cells || row.Cells || [];
        return headers.map(header => {
            const cell = cells.find(c => (c.featureName || c.FeatureName) === header);
            const value = cell?.expectedValue ?? cell?.ExpectedValue ?? '';
            return String(value).replace(/\t/g, ' ').replace(/\r?\n/g, ' ');
        }).join('\t');
    });
    return [headerLine, ...rowLines].join('\n');
};
